/* Where the reward markers sit on the rail, and how full it is.
 *
 * Split out of mini-cart.js so it can be driven from a bench without a store
 * behind it. Adding to a real cart to see a progress bar move writes to
 * analytics, the pixel and the abandoned-cart flows; this file is the whole of
 * the behaviour worth iterating on, and it needs nothing but numbers.
 *
 * The spacing is deliberately NOT proportional to money.
 *
 * On a linear rail the last tier is always the furthest away, so the rail is
 * at its least persuasive exactly when there is most left to earn. Here each
 * span between two markers is weighted by what it is for: a span already
 * earned contracts, the span being worked on opens up, and spans beyond it sit
 * at their natural width. Clearing a marker therefore pulls the next one back
 * toward it - the thing left to do always looks close, and the distance
 * already covered reads as ground gained rather than as a short start.
 *
 * It stays honest because the fill is still the true fraction of money within
 * the active span; only the horizontal scale is editorial, and the amounts are
 * printed beside the markers.
 */
(function () {
  'use strict';

  var DONE = 0.58;   /* a span already earned, contracted  */
  var ACTIVE = 1.75; /* the span in play, opened up        */
  var AHEAD = 1;     /* everything after it                */

  /* tiers: [{ amount (cents), label }], lowest first, first amount 0.
   * total: cart total in cents.
   *
   * Returns { positions: [% per tier], fillPct, nextIndex, metCount }.
   * nextIndex is null once every tier is met. */
  function layout(tiers, total) {
    var n = tiers.length;
    if (!n) return { positions: [], fillPct: 0, nextIndex: null, metCount: 0 };
    if (n === 1) return { positions: [0], fillPct: 100, nextIndex: null, metCount: 1 };

    var nextIndex = null;
    var i;
    for (i = 0; i < n; i++) {
      if (tiers[i].amount > total) { nextIndex = i; break; }
    }
    var metCount = nextIndex === null ? n : nextIndex;

    /* Weight each span. Span i runs from tier i to tier i+1, and is the active
       one when tier i+1 is the next unmet marker. */
    var weights = [];
    var sum = 0;
    for (i = 0; i < n - 1; i++) {
      var w = tiers[i + 1].amount <= total ? DONE : (nextIndex === i + 1 ? ACTIVE : AHEAD);
      weights.push(w);
      sum += w;
    }

    var positions = [0];
    var acc = 0;
    for (i = 0; i < weights.length; i++) {
      acc += weights[i];
      positions.push((acc / sum) * 100);
    }

    /* The fill is the real fraction of money inside the active span, mapped
       onto that span's drawn width - so the bar can be read against the
       amounts even though the scale is not linear. */
    var fillPct;
    if (nextIndex === null) {
      fillPct = 100;
    } else if (nextIndex === 0) {
      fillPct = 0;
    } else {
      var from = tiers[nextIndex - 1].amount;
      var to = tiers[nextIndex].amount;
      var within = to > from ? (total - from) / (to - from) : 0;
      if (within < 0) within = 0;
      if (within > 1) within = 1;
      var a = positions[nextIndex - 1];
      var b = positions[nextIndex];
      fillPct = a + within * (b - a);
    }

    return { positions: positions, fillPct: fillPct, nextIndex: nextIndex, metCount: metCount };
  }


  /* Marker glyphs. They live here rather than in the drawer script so the
     bench draws exactly what the shop draws. Stroked, currentColor, no fills -
     a marker is tiny and inverts to white the moment its tier is cleared. */
  var ICONS = {
      "gift": "<path d=\"M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z\"/>",
      "discount": "<path d=\"M20.6 13.4 12 22l-9-9V3h10l7.6 7.6a2 2 0 0 1 0 2.8z\"/><circle cx=\"7.5\" cy=\"7.5\" r=\"1.3\"/>",
      "shipping": "<path d=\"M1 3h15v13H1zM16 8h4l3 3v5h-7\"/><circle cx=\"5.5\" cy=\"18.5\" r=\"2.5\"/><circle cx=\"18.5\" cy=\"18.5\" r=\"2.5\"/>",
      "star": "<path d=\"M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4 6.2 20.4l1.1-6.5L2.6 9.3l6.5-.9z\"/>",
      "warranty": "<path d=\"M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5z\"/><path d=\"M9 12l2 2 4-4\"/>"
  };

  function icon(name) {
    var d = ICONS[name];
    return d ? '<svg class="cart-drawer__tier-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">%s</svg>'.replace('%s', d) : '';
  }

  /* A share of the rail for each marker's label, as percentages that sum to
     100. A label centred on its marker grows both ways as its text gets
     longer, so two wordy rewards reach into each other - which is what
     overlapping labels on the rail actually are.

     A slot runs from halfway back to the previous marker to halfway on to the
     next, so adjacent slots touch and never cross. A label laid in its own
     slot cannot reach its neighbour however long it is; it wraps instead. The
     slot still contains its marker, so the label stays under what it names. */
  function slots(positions) {
    var n = positions.length;
    if (!n) return [];
    if (n === 1) return [100];
    var edges = [0];
    for (var i = 1; i < n; i++) edges.push((positions[i - 1] + positions[i]) / 2);
    edges.push(100);
    var out = [];
    for (var j = 0; j < n; j++) out.push(edges[j + 1] - edges[j]);
    return out;
  }

  window.KMCartTiers = { layout: layout, slots: slots, icon: icon, ICONS: ICONS, WEIGHTS: { DONE: DONE, ACTIVE: ACTIVE, AHEAD: AHEAD } };
}());
